"use strict";
/**
 * This file contains service functions for character-related operations in the application.
 * It includes functions for interacting with characters, conversations, user profiles, and image generation.
 * The services make use of axios for HTTP requests and handle various API endpoints.
 *
 * Key functionalities include:
 * - Character management (creation, editing, deletion, fetching)
 * - Conversation handling (creating, updating, deleting, fetching messages)
 * - User operations (syncing, profile management, quota checking)
 * - Image generation for characters
 * - Authentication and authorization checks
 *
 * The file uses different interceptors for various types of API calls (anonymous, authenticated, cached).
 * It also includes error handling and type definitions for API responses.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.updatePrompt = exports.getAuthorCharacters = exports.updateConversations = exports.syncUser = exports.getUserStatus = exports.getUserQuota = exports.getUserProfileService = exports.getPrompts = exports.getNewConversations = exports.getOrCreateConversations = exports.getImagePrompts = exports.getConversationMessages = exports.getCharacterPrompts = exports.getCharacterProfile = exports.getCharacterList = exports.getCharacterImages = exports.getCharacterImageModels = exports.getCharacterGeneratedImages = exports.getCategoryList = exports.getAllConversations = exports.generateCharacterDetails = exports.deleteConversations = exports.deleteCharacter = exports.createNewSyncUser = exports.createExistingUser = exports.createEditCharacter = exports.checkUsernameUniqueness = void 0;
const axios_1 = require("axios");
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const moment_1 = __importDefault(require("moment"));
const apis_1 = require("../common/apis");
const application_host_1 = require("../common/application-host");
const interceptorsAnon_1 = require("./interceptorsAnon");
const interceptorsAnonAuth_1 = require("./interceptorsAnonAuth");
const interceptorsCached_1 = require("./interceptorsCached");
const error_1 = require("../common/error");
const constants_1 = require("../common/constants");
/**
 * Fetches a list of characters based on the provided category
 * @param category - Optional category to filter characters
 * @returns Promise resolving to an APIResponse containing an array of CharacterType
 */
const getCharacterList = async (category) => {
    const token = await async_storage_1.default.getItem("authToken");
    const callApi = token ? interceptorsCached_1.cachedAuthCall : interceptorsAnon_1.cachedNetworkCall;
    return new Promise(async (resolve) => {
        await callApi
            .get(`${apis_1.APP_CONSTANTS.character.characters}?category=${category && category !== "" && category?.toLowerCase() !== "all"
            ? category
            : ""}`)
            .then(function (response) {
            const json = response;
            // console.log(`Got: ${JSON.stringify(json, 1)}`);
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.getCharacterList = getCharacterList;
/**
 * Fetches the list of available categories
 * @returns Promise resolving to the category list
 */
const getCategoryList = async () => {
    return new Promise(async (resolve) => {
        await interceptorsAnon_1.cachedNetworkCall
            .get(apis_1.APP_CONSTANTS.character.categories)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.getCategoryList = getCategoryList;
/**
 * Fetches available prompts
 * @returns Promise resolving to the prompts or a RequestError
 */
const getPrompts = async () => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(apis_1.APP_CONSTANTS.prompts.get)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.getPrompts = getPrompts;
/**
 * Updates prompts with the provided promptType and context
 * @param promptType - Type of the prompt to update
 * @param context - Context for the prompt
 * @returns Promise resolving to the updated prompt or a RequestError
 */
const updatePrompt = async (promptType, context) => {
    return new Promise(async (resolve, _reject) => {
        const body = { promptType, context };
        await interceptorsCached_1.cachedAuthCall
            .post(apis_1.APP_CONSTANTS.prompts.update, body)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.updatePrompt = updatePrompt;
/**
 * Fetches prompts for a specific character
 * @param characterId - ID of the character
 * @returns Promise resolving to the character prompts or a RequestError
 */
const getCharacterPrompts = async (characterId) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.prompts.get}/${characterId}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.getCharacterPrompts = getCharacterPrompts;
/**
 * Fetches image prompts for a specific character
 * @param characterId - ID of the character
 * @returns Promise resolving to the image prompts
 */
const getImagePrompts = async (characterId) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.prompts.get}/${characterId}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.getImagePrompts = getImagePrompts;
/**
 * Creates or retrieves conversations based on the provided parameters
 * @param params - ConversationDTO containing conversation details
 * @returns Promise resolving to an APIResponse containing ConversationMeta array or a RequestError
 */
const getOrCreateConversations = async (params) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .post(apis_1.APP_CONSTANTS.conversation.index, params).then(function (response) {
            resolve(response);
        }).catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.getOrCreateConversations = getOrCreateConversations;
/**
 * Creates new conversations based on the provided parameters
 * @param params - ConversationDTO containing conversation details
 * @returns Promise resolving to the new conversations
 */
const getNewConversations = async (params) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .post(apis_1.APP_CONSTANTS.conversation.index, params)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            console.log("getNewConversations error:", error);
            resolve(error);
        });
    });
};
exports.getNewConversations = getNewConversations;
/**
 * Generates character details based on the provided parameters
 * @param params - GenerateCharacterPayloadType containing character generation details
 * @returns Promise resolving to CharacterDetails or a RequestError
 */
const generateCharacterDetails = async (params) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsAnonAuth_1.networkCall
            .post(apis_1.APP_CONSTANTS.character.generateCharacter, params).then(function (response) {
            const json = response;
            resolve(json);
        }).catch(function (error) {
            resolve(new error_1.RequestError(error));
        });
    });
};
exports.generateCharacterDetails = generateCharacterDetails;
/**
 * Fetches all conversations for a specific user
 * @param userId - ID of the user
 * @returns Promise resolving to an APIResponse containing conversations or a RequestError
 */
const getAllConversations = async (userId) => {
    return new Promise(async (resolve, reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.conversation.index}/user/${userId}?date=${(0, moment_1.default)().unix()}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized ?
                    'Session Out' : undefined
            }));
        });
    });
};
exports.getAllConversations = getAllConversations;
/**
 * Synchronizes user data
 * @param userId - ID of the user to sync
 * @returns Promise resolving to the sync response or a RequestError
 */
const syncUser = async (userId, products = [application_host_1.APP_PRODUCT]) => {
    console.log("syncUser----------", userId);
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .post(`${apis_1.APP_CONSTANTS.auth.sync}`, { id: userId, products })
            .then(function (response) {
            console.log("syncUser----------", response);
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.syncUser = syncUser;
/**
 * Creates a new synchronized user
 * @returns Promise resolving to the new user data
 */
const createNewSyncUser = async () => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsAnonAuth_1.networkCall
            .post(`${apis_1.APP_CONSTANTS.auth.sync}`, {})
            .then(function (response) {
            console.log("createNewSyncUser-----", response);
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.createNewSyncUser = createNewSyncUser;
/**
 * Creates an existing user in the system
 * @param payload - Object containing user details (id, email, name, username)
 * @returns Promise resolving to an AxiosResponse
 */
const createExistingUser = async (payload) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .post(`${apis_1.APP_CONSTANTS.auth.sync}`, payload)
            .then(function (response) {
            console.log("createExistingUser-----", response);
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.createExistingUser = createExistingUser;
/**
 * Checks if a username is unique
 * @param payload - Object containing the username to check
 * @returns Promise resolving to the uniqueness check result
 */
const checkUsernameUniqueness = (payload) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsAnon_1.cachedNetworkCall
            .post(`${apis_1.APP_CONSTANTS.auth.checkUsername}`, payload)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.checkUsernameUniqueness = checkUsernameUniqueness;
/**
 * Fetches the user profile
 * @returns Promise resolving to an AxiosResponse, UnauthorizedRequest, or RequestError
 */
const getUserProfileService = async () => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(apis_1.APP_CONSTANTS.auth.userProfile)
            .then(function (response) {
            const json = response;
            resolve(json);
        }).catch(function (error) {
            console.log(error);
            switch (error.response?.status) {
                case axios_1.HttpStatusCode.Unauthorized:
                    resolve(new error_1.UnauthorizedRequest(error));
                    break;
                default:
                    resolve(new error_1.RequestError(error));
            }
        });
    });
};
exports.getUserProfileService = getUserProfileService;
/**
 * Fetches the status of a user
 * @param userId - ID of the user
 * @returns Promise resolving to the user status
 */
const getUserStatus = async (userId) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.auth.users}?userId=${userId}`)
            .then(function (response) {
            console.log("Get status:", response);
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.getUserStatus = getUserStatus;
/**
 * Creates or edits a character
 * @param params - Object containing character details
 * @param isEdit - Boolean indicating whether this is an edit operation
 * @returns Promise resolving to an APIResponse containing the Character
 */
const createEditCharacter = async (params, isEdit) => {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(params));
    return new Promise(async (resolve, reject) => {
        const configs = {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        };
        if (isEdit) {
            //edit existing character
            interceptorsCached_1.cachedAuthCall
                .put(`${apis_1.APP_CONSTANTS.character.edit}`, formData, configs)
                .then((response) => {
                const json = response;
                resolve(json);
            })
                .catch((error) => {
                console.log(error);
                reject(error);
            });
        }
        else {
            //New character
            interceptorsCached_1.cachedAuthCall
                .post(`${apis_1.APP_CONSTANTS.character.create}`, formData, configs)
                .then((response) => {
                const json = response;
                resolve(json);
            })
                .catch((error) => {
                reject(error);
                console.log(error);
            });
        }
    });
};
exports.createEditCharacter = createEditCharacter;
/**
 * Fetches available image models for character creation
 * @returns Promise resolving to an APIResponse containing an array of ImageModel
 */
const getCharacterImageModels = async () => {
    return new Promise(async (resolve, reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${application_host_1.ADD_API}${apis_1.APP_CONSTANTS.imageModel}`)
            .then((response) => {
            const json = response;
            resolve(json);
        })
            .catch((error) => {
            console.log("Response:',error", error);
            reject(error);
            console.log(error);
        });
    });
};
exports.getCharacterImageModels = getCharacterImageModels;
/**
 * Generates character images based on the provided prompt
 * @param params - ImagePrompt containing details for image generation
 * @returns Promise resolving to the generated images or a RequestError
 */
const getCharacterImages = async (params) => {
    return new Promise(async (resolve, reject) => {
        await interceptorsCached_1.cachedAuthCall
            .post(`${application_host_1.ADD_API}characters/images`, params)
            .then((response) => {
            const json = response;
            resolve(json);
        })
            .catch((error) => {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.getCharacterImages = getCharacterImages;
/**
 * Fetches generated images for a specific character
 * @param characterId - ID of the character
 * @returns Promise resolving to an APIResponse containing CharacterImageGeneration
 */
const getCharacterGeneratedImages = async (characterId) => {
    return new Promise(async (resolve, reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${application_host_1.ADD_API}characters/images/${characterId}`).then((response) => {
            const json = response;
            resolve(json);
        }).catch((error) => {
            console.log("Response:',error", error);
            reject(error);
            console.log(error);
        });
    });
};
exports.getCharacterGeneratedImages = getCharacterGeneratedImages;
/**
 * Fetches messages for a specific conversation
 * @param id - ID of the conversation
 * @param limit - Optional limit for the number of messages to fetch
 *
 * @param id
 * @returns
 */
const getConversationMessages = async (id, limit) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.messeges.get}/${id}?tst=${(0, moment_1.default)().unix()}&limit=${limit || constants_1.MESSAGES_PER_PAGE}&offset=0&sort=DESC`).then(function (response) {
            if (!response.success) {
                resolve(new error_1.APIError(response.error?.message));
            }
            const json = response;
            resolve(json);
        }).catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized ?
                    'Session Out' : undefined
            }));
        });
    });
};
exports.getConversationMessages = getConversationMessages;
const updateConversations = async (id, params) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .put(`${apis_1.APP_CONSTANTS.conversation.update}/${id}`, params)
            .then(function (response) {
            if (!response.success) {
                resolve(new error_1.APIError(response.error?.message));
            }
            else {
                const json = response;
                resolve(json);
            }
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error));
        });
    });
};
exports.updateConversations = updateConversations;
const deleteConversations = async (id, clear) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .delete(`${apis_1.APP_CONSTANTS.conversation.index}/${id}${clear ? "/clear" : ""}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.deleteConversations = deleteConversations;
const deleteCharacter = async (id) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .delete(`${apis_1.APP_CONSTANTS.character.index}/${id}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.deleteCharacter = deleteCharacter;
/**
 *
 * @param id
 * @returns
 */
const getCharacterProfile = async (id) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.character.characters}/${id}`)
            .then(function (response) {
            if (response.error?.code === axios_1.HttpStatusCode.NotFound) {
                resolve(new error_1.APIError(response.error.message, { reason: 'Not Found' }));
            }
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized ?
                    'Session Out' : undefined
            }));
            if (error.response?.status === axios_1.HttpStatusCode.NotFound) {
            }
        });
    });
};
exports.getCharacterProfile = getCharacterProfile;
/**
 *
 * @param id
 * @returns
 */
const getAuthorCharacters = async (id) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.character.authorCharacters}/${id}`)
            .then(function (response) {
            const json = response;
            resolve(json);
        })
            .catch(function (error) {
            resolve(error);
        });
    });
};
exports.getAuthorCharacters = getAuthorCharacters;
const getUserQuota = async (userId) => {
    return new Promise(async (resolve, _reject) => {
        await interceptorsCached_1.cachedAuthCall
            .get(`${apis_1.APP_CONSTANTS.auth.quota}/${userId}`)
            .then(function (response) {
            const json = response;
            resolve(json.data);
        })
            .catch(function (error) {
            resolve(new error_1.RequestError(error, {
                reason: error.response?.status === axios_1.HttpStatusCode.Unauthorized
                    ? 'Session Out' : undefined
            }));
        });
    });
};
exports.getUserQuota = getUserQuota;
